import { AudioClipBox, NoteClipBox, NoteEventCollectionBox, TrackBox, ValueClipBox, ValueEventCollectionBox } from "@opendaw/studio-boxes";
import { PPQN, TimeBase } from "@opendaw/lib-dsp";
import { ColorCodes, TrackType } from "@opendaw/studio-adapters";
import { asInstanceOf, isDefined, panic, UUID } from "@opendaw/lib-std";
import { Facade, Props } from "../Common";
import { Guard } from "../Guard";
import { NoteEvents, ValueEvents } from "./Events";
import { AudioContents } from "./AudioContents";
import { AudioFiles } from "../AudioFiles";
import { TrackImpls } from "./Tracks";
import { NoteRegionImpl, ValueRegionImpl } from "./Regions";
export class ClipFacade extends Facade {
    constructor(context, box) {
        super(context, box);
        const { triggerMode } = box;
        this.bind({
            duration: box.duration, mute: box.mute, label: box.label, hue: box.hue,
            launch: {
                loop: triggerMode.loop, reverse: triggerMode.reverse, speed: triggerMode.speed,
                quantise: triggerMode.quantise, trigger: triggerMode.trigger
            }
        });
    }
    get index() { return this.box.index.getValue(); }
    set index(value) {
        const index = Guard.int32("index", value, "index");
        const taken = Clips.list(this.context, this.trackBox).some(clip => clip.box !== this.box && clip.index === index);
        if (taken) {
            panic(new RangeError(`index: slot ${index} is already taken`));
        }
        this.context.edit(() => this.box.index.setValue(index));
    }
    get trackBox() {
        return asInstanceOf(this.box.clips.targetVertex.unwrap("clip has no track").box, TrackBox);
    }
}
export class NoteClipImpl extends ClipFacade {
    static wrap(context, box) {
        return context.facade(box, () => new NoteClipImpl(context, box));
    }
    constructor(context, box) { super(context, box); }
    get track() { return TrackImpls.wrap(this.context, this.trackBox); }
    get noteEvents() {
        return new NoteEvents(this.context, asInstanceOf(this.box.events.targetVertex.unwrap("clip has no events").box, NoteEventCollectionBox));
    }
    get events() { return this.noteEvents.list(); }
    addEvent(props) { return this.noteEvents.add(props); }
    addEvents(events) { return this.noteEvents.addAll(events); }
    clearEvents() { this.noteEvents.clear(); }
}
export class ValueClipImpl extends ClipFacade {
    static wrap(context, box) {
        return context.facade(box, () => new ValueClipImpl(context, box));
    }
    constructor(context, box) { super(context, box); }
    get track() { return TrackImpls.wrap(this.context, this.trackBox); }
    get valueEvents() {
        return new ValueEvents(this.context, asInstanceOf(this.box.events.targetVertex.unwrap("clip has no events").box, ValueEventCollectionBox));
    }
    get events() { return this.valueEvents.list(); }
    addEvent(props) { return this.valueEvents.add(props); }
    addEvents(events) { return this.valueEvents.addAll(events); }
    clearEvents() { this.valueEvents.clear(); }
}
export class AudioClipImpl extends ClipFacade {
    static wrap(context, box) {
        return context.facade(box, () => new AudioClipImpl(context, box));
    }
    constructor(context, box) {
        super(context, box);
        this.bind({ gain: box.gain, waveformOffset: box.waveformOffset });
    }
    get track() { return TrackImpls.wrap(this.context, this.trackBox); }
    get sample() { return AudioContents.sample(this.context, this.box); }
    get playback() { return AudioContents.playback(this.box); }
    get transientPlayMode() { return AudioContents.transientPlayMode(this.box); }
    set transientPlayMode(value) { AudioContents.setTransientPlayMode(this.context, this.box, value); }
    get playbackRate() { return AudioContents.playbackRate(this.box); }
    set playbackRate(value) { AudioContents.setPlaybackRate(this.context, this.box, value); }
    get transpose() { return AudioContents.transpose(this.box); }
    set transpose(value) { AudioContents.setTranspose(this.context, this.box, value); }
}
export var Clips;
(function (Clips) {
    Clips.wrap = (context, box) => {
        if (box instanceof NoteClipBox) {
            return NoteClipImpl.wrap(context, box);
        }
        if (box instanceof AudioClipBox) {
            return AudioClipImpl.wrap(context, box);
        }
        if (box instanceof ValueClipBox) {
            return ValueClipImpl.wrap(context, box);
        }
        return panic(`${box.name} is not a clip`);
    };
    Clips.list = (context, trackBox) => trackBox.clips.pointerHub.incoming()
        .map(({ box }) => Clips.wrap(context, box))
        .sort((a, b) => a.index - b.index);
    Clips.resolveIndex = (context, trackBox, index) => {
        const taken = new Set(Clips.list(context, trackBox).map(clip => clip.index));
        if (isDefined(index)) {
            const validated = Guard.int32("index", index, "index");
            if (taken.has(validated)) {
                return panic(new RangeError(`index: slot ${validated} is already taken`));
            }
            return validated;
        }
        let free = 0;
        while (taken.has(free)) {
            free++;
        }
        return free;
    };
    const mirrorCollection = (mirror, expected) => {
        if (expected === TrackType.Notes && (mirror instanceof NoteRegionImpl || mirror instanceof NoteClipImpl)) {
            return mirror.noteEvents.collection;
        }
        if (expected === TrackType.Value && (mirror instanceof ValueRegionImpl || mirror instanceof ValueClipImpl)) {
            return mirror.valueEvents.collection;
        }
        return panic(new TypeError(`mirror: expected a region or clip of the same type, got ${Guard.describe(mirror)}`));
    };
    Clips.createNoteClip = (context, trackBox, props) => {
        if (trackBox.type.getValue() !== TrackType.Notes) {
            return panic(new TypeError("Cannot add a note clip to a non-note track"));
        }
        return context.edit(() => {
            const index = Clips.resolveIndex(context, trackBox, props?.index);
            const collection = isDefined(props?.mirror)
                ? mirrorCollection(props.mirror, TrackType.Notes)
                : NoteEventCollectionBox.create(context.boxGraph, UUID.generate());
            const box = NoteClipBox.create(context.boxGraph, UUID.generate(), box => {
                box.index.setValue(index);
                box.duration.setValue(PPQN.Bar);
                box.hue.setValue(ColorCodes.forTrackType(TrackType.Notes));
                box.clips.refer(trackBox.clips);
                box.events.refer(collection.owners);
            });
            const clip = Props.apply(NoteClipImpl.wrap(context, box), Props.without(props, "mirror", "index", "launch"));
            if (isDefined(props?.launch)) {
                Props.apply(clip.launch, props.launch, "launch");
            }
            return clip;
        });
    };
    Clips.createValueClip = (context, trackBox, props) => {
        if (trackBox.type.getValue() !== TrackType.Value) {
            return panic(new TypeError("Cannot add an automation clip to a non-automation track"));
        }
        return context.edit(() => {
            const index = Clips.resolveIndex(context, trackBox, props?.index);
            const collection = isDefined(props?.mirror)
                ? mirrorCollection(props.mirror, TrackType.Value)
                : ValueEventCollectionBox.create(context.boxGraph, UUID.generate());
            const box = ValueClipBox.create(context.boxGraph, UUID.generate(), box => {
                box.index.setValue(index);
                box.duration.setValue(PPQN.Bar);
                box.hue.setValue(ColorCodes.forTrackType(TrackType.Value));
                box.clips.refer(trackBox.clips);
                box.events.refer(collection.owners);
            });
            const clip = Props.apply(ValueClipImpl.wrap(context, box), Props.without(props, "mirror", "index", "launch"));
            if (isDefined(props?.launch)) {
                Props.apply(clip.launch, props.launch, "launch");
            }
            return clip;
        });
    };
    Clips.createAudioClip = (context, trackBox, sample, props) => {
        if (trackBox.type.getValue() !== TrackType.Audio) {
            return panic(new TypeError("Cannot add an audio clip to a non-audio track"));
        }
        return context.edit(() => {
            const fileBox = AudioFiles.use(context, sample);
            const validated = AudioFiles.validate(sample);
            const playback = isDefined(props?.playback) ? AudioContents.validatePlayback(props.playback) : AudioContents.defaultPlayback(validated);
            const projectBpm = context.skeleton.mandatoryBoxes.timelineBox.bpm.getValue();
            const index = Clips.resolveIndex(context, trackBox, props?.index);
            const duration = Guard.float32("positive", props?.duration ?? AudioContents.defaultDuration(validated, playback, projectBpm), "duration");
            const timeBase = playback === "no-sync" ? TimeBase.Seconds : TimeBase.Musical;
            const playMode = AudioContents.createPlayMode(context, playback, duration, validated.duration, {
                transientPlayMode: props?.transientPlayMode, playbackRate: props?.playbackRate, transpose: props?.transpose
            });
            const collection = ValueEventCollectionBox.create(context.boxGraph, UUID.generate());
            const box = AudioClipBox.create(context.boxGraph, UUID.generate(), box => {
                box.index.setValue(index);
                box.duration.setValue(duration);
                box.hue.setValue(ColorCodes.forTrackType(TrackType.Audio));
                box.label.setValue(validated.name);
                box.clips.refer(trackBox.clips);
                box.file.refer(fileBox);
                box.events.refer(collection.owners);
                box.timeBase.setValue(timeBase);
                if (isDefined(playMode)) {
                    box.playMode.refer(playMode);
                }
            });
            const rest = Props.without(props, "playback", "index", "duration", "transientPlayMode", "playbackRate", "transpose", "launch");
            const clip = Props.apply(AudioClipImpl.wrap(context, box), rest);
            if (isDefined(props?.launch)) {
                Props.apply(clip.launch, props.launch, "launch");
            }
            return clip;
        });
    };
})(Clips || (Clips = {}));
